import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../api/client';
import { FleetVersionSummary } from '../../types';
import { MetricCard } from '../../components/common/MetricCard';
import { Layers, RefreshCw, Wifi, WifiOff, HelpCircle, AlertTriangle } from 'lucide-react';

// Monitoring the fleet by version, not by camera.
//
// The fleet is a few versions — The Eye 4G, The Eye WiFi, The Eye Mini Solar —
// and the question worth watching is how each VERSION is doing. A version where
// most cameras have gone dark at once is a firmware or hardware problem; one
// dark camera is a support ticket. This answers the first; the Cameras page
// answers the second. Data from GET /v2/admin/devices/versions.
export const VersionsPage: React.FC = () => {
  const [data, setData] = useState<FleetVersionSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await api.getFleetVersions());
    } catch (e: any) {
      setError(e?.message || 'Could not load camera versions.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onlinePct = data && data.totals.cameras > 0
    ? Math.round((data.totals.online / data.totals.cameras) * 100) : 0;

  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <Layers size={20} color="var(--primary-light)" />
          <h2 style={{ fontSize: 17, fontWeight: 600, color: 'var(--text-main)' }}>Camera Versions</h2>
        </div>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', maxWidth: 720 }}>
          The fleet grouped by version. A version going dark together is a firmware or hardware
          signal; one dark camera is a ticket. Never-reported is counted apart from offline —
          a provisioning gap is not an outage.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 }}>
        <button className="btn btn-secondary btn-sm" onClick={load} disabled={loading}>
          <RefreshCw size={14} className={loading ? 'spin' : ''} /> <span>Refresh</span>
        </button>
      </div>

      {error && (
        <div data-testid="versions-error"
             style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger-border)', color: 'var(--danger-text)', padding: '10px 14px', borderRadius: 'var(--radius-md)', marginBottom: 16, fontSize: 13, display: 'flex', gap: 8, alignItems: 'center' }}>
          <AlertTriangle size={15} /> <span>{error}</span>
        </div>
      )}

      {data && (
        <>
          <div className="kpi-grid" style={{ marginBottom: 18 }}>
            <MetricCard label="Versions" value={data.totals.versions} icon={<Layers size={15} />} />
            <MetricCard label="Cameras" value={data.totals.cameras.toLocaleString()} />
            <MetricCard label="Reporting now" value={`${data.totals.online} · ${onlinePct}%`} icon={<Wifi size={15} />} />
          </div>

          <div style={{ display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
            {data.versions.map(v => {
              const pct = v.cameras > 0 ? Math.round((v.online / v.cameras) * 100) : 0;
              const solid = pct >= 70 ? 'var(--success-solid, #3FBFB6)'
                : pct >= 30 ? 'var(--warning-solid, #F1A33F)' : 'var(--danger-solid, #E5484D)';
              return (
                <div key={v.model} data-testid="version-card" className="card" style={{ padding: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                    <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: 6 }}>
                      {v.model === 'Unknown' && <HelpCircle size={14} color="var(--text-muted)" />}
                      {v.model === 'Unknown' ? 'Unknown version' : v.model}
                    </h3>
                    <span style={{ fontSize: 14, fontWeight: 600, color: solid }}>{pct}%</span>
                  </div>
                  <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '2px 0 10px' }}>
                    {v.cameras.toLocaleString()} {v.cameras === 1 ? 'camera' : 'cameras'}
                  </p>

                  <div style={{ height: 8, borderRadius: 999, background: 'var(--bg-surface-raised, rgba(255,255,255,0.06))', overflow: 'hidden', marginBottom: 12 }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: solid }} />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, textAlign: 'center' }}>
                    <Split icon={<Wifi size={13} color="var(--success-text)" />} label="Reporting" value={v.online} />
                    <Split icon={<WifiOff size={13} color="var(--text-muted)" />} label="Dark" value={v.offline} />
                    <Split label="Never seen" value={v.neverReported} warn={v.neverReported > 0} />
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

const Split: React.FC<{ icon?: React.ReactNode; label: string; value: number; warn?: boolean }> = ({ icon, label, value, warn }) => (
  <div>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
      {icon}
      <span style={{ fontSize: 16, fontWeight: 600, color: warn ? 'var(--warning-text)' : 'var(--text-main)' }}>
        {value.toLocaleString()}
      </span>
    </div>
    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{label}</div>
  </div>
);
